import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import { GameScene } from '../../GameScene';
import { CandleBlockEntity } from '../../entities/BlockEntity';
import { app } from '../../app';
import { getMatch2AssetPath } from '../../util/assets';
import { Container, Sprite, randomRange } from '../../../odie';
import gsap from 'gsap';

// Candle is the id of coffee cup box block
type CandleBlockEffectOptions = {
  entity: CandleBlockEntity;
};

export class CandleBlockEffect {
  private readonly scene: GameScene;
  private readonly entity: CandleBlockEntity;

  constructor(scene: GameScene, options: CandleBlockEffectOptions) {
    this.scene = scene;
    this.entity = options.entity;
  }

  public async animateCupBreak(refCup: Sprite) {
    if (!refCup.texture) return;
    const container = new Container();
    const position = this.entity.c.position.mapPosition.clone();
    const overlay = spawnOverlayEntity(
      this.scene,
      position,
      this.entity.c.block.props,
      () => container,
      'foreground',
    );

    const cup = new Sprite(refCup.texture);
    cup.anchorX = cup.anchorY = 0.5;
    cup.x = refCup.x + refCup.width / 2;
    cup.y = refCup.y + refCup.height / 2;
    container.addChild(cup);

    const x = cup.x + randomRange(-100, 100);
    const direction = x > cup.x ? -1 : 1;
    const yUp = cup.y - 50;
    const yDown = cup.y + 100;
    const duration = 0.8;

    gsap.to(cup, {
      x,
      scaleX: 2,
      scaleY: 2,
      rotation: 9 * direction,
      duration,
      ease: 'linear',
    });

    gsap.to(cup, {
      alpha: 0,
      duration: duration * 0.3,
      delay: duration * 0.7,
      ease: 'linear',
    });

    await gsap.to(cup, {
      y: yUp,
      duration: duration * 0.4,
      ease: 'quad.out',
    });
    await gsap.to(cup, {
      y: yDown,
      duration: duration * 0.6,
      ease: 'quad.in',
    });

    container.removeChild(cup);
    despawnOverlayEntity(this.scene, overlay);
  }

  public async animateBoxBreak() {
    const particles = app.particles.create({
      textures: [
        getMatch2AssetPath('candles/block.candle.piece1.png'),
        getMatch2AssetPath('candles/block.candle.piece2.png'),
      ],
      rate: 0,
      limit: 6,
      emitterDuration: 0,
      duration: 1,
      velocity: [
        { x: 0, y: -300 },
        { x: 0, y: -600 },
      ],
      scale: [
        { x: 0.7, y: 0.7 },
        { x: 1.3, y: 1.3 },
      ],
      behaviors: [
        { type: 'explode', magnitude: [300, 500], applyRotation: true },
        { type: 'gravity', gravity: { x: 0, y: 2500 } },
        { type: 'scale', to: 0 },
        { type: 'kinematic', directionalRotation: true },
      ],
    });

    const position = this.entity.c.position.mapPosition.clone();
    const overlay = spawnOverlayEntity(
      this.scene,
      position,
      this.entity.c.block.props,
      () => particles.view,
      'foreground',
    );
    await particles.start();
    despawnOverlayEntity(this.scene, overlay);
  }
}
